import { cctvService, type Cctv } from "@safers/api";
import { ApiError } from "@safers/api";

/**
 * REST 호출은 `@safers/api` 가 한다. 여기는 받은 바이트를 다루는 쪽이다.
 * `getWHEPUrl` 은 백엔드가 아니라 MediaMTX 로 가는 주소다.
 */
function getWHEPUrl(streamName: string): string {
  return `/webrtc/${encodeURIComponent(streamName)}/whep`;
}

const MAX_DOWNLOAD_MINUTES = 2 * 60;

const BYTES_PER_VIDEO_SECOND_MIN = 128 * 1024;
const BYTES_PER_VIDEO_SECOND_MAX = 448 * 1024;

const MAX_DOWNLOAD_BYTES = 2 * 1024 * 1024 * 1024;

const DOWNLOAD_SPEEDUP = 12;
const DOWNLOAD_STARTUP_SECONDS = 15;

export interface DownloadEstimate {
  minBytes: number;
  maxBytes: number;
  seconds: number;
}

function estimateDownload(rangeMinutes: number): DownloadEstimate {
  const videoSeconds = rangeMinutes * 60;
  return {
    minBytes: videoSeconds * BYTES_PER_VIDEO_SECOND_MIN,
    maxBytes: videoSeconds * BYTES_PER_VIDEO_SECOND_MAX,
    seconds: DOWNLOAD_STARTUP_SECONDS + videoSeconds / DOWNLOAD_SPEEDUP,
  };
}

export interface DownloadProgress {
  receivedBytes: number;
  /** 재생 시각으로 잰 진행률. MP4 박스를 못 읽으면 null */
  ratio: number | null;
  /** 지금까지의 비트레이트로 추정한 전체 크기. 초반 3초 전에는 null */
  projectedBytes: number | null;
}

function createFragmentClock() {
  let buffer: Uint8Array = new Uint8Array(0);
  let skipRemaining = 0;
  let timescale: number | null = null;
  let mediaSeconds = 0;
  let broken = false;

  function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
    const out = new Uint8Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
  }

  function findBox(view: DataView, type: string): number {
    for (let i = 0; i + 8 <= view.byteLength; i++) {
      if (
        view.getUint8(i + 4) === type.charCodeAt(0) &&
        view.getUint8(i + 5) === type.charCodeAt(1) &&
        view.getUint8(i + 6) === type.charCodeAt(2) &&
        view.getUint8(i + 7) === type.charCodeAt(3)
      ) {
        return i + 8;
      }
    }
    return -1;
  }

  return {
    push(chunk: Uint8Array): void {
      if (broken) return;
      try {
        let rest = chunk;
        if (skipRemaining > 0) {
          const n = Math.min(skipRemaining, rest.length);
          skipRemaining -= n;
          rest = rest.subarray(n);
          if (rest.length === 0) return;
        }
        buffer = concat(buffer, rest);

        for (;;) {
          if (buffer.length < 8) return;
          const head = new DataView(buffer.buffer, buffer.byteOffset, 8);
          let size = head.getUint32(0);
          const type = String.fromCharCode(
            head.getUint8(4),
            head.getUint8(5),
            head.getUint8(6),
            head.getUint8(7)
          );

          if (size === 1) {
            if (buffer.length < 16) return;
            const big = new DataView(buffer.buffer, buffer.byteOffset, 16).getBigUint64(8);
            size = Number(big);
          }
          if (size < 8) {
            broken = true;
            return;
          }

          if (type !== "moov" && type !== "moof") {
            if (buffer.length >= size) {
              buffer = buffer.subarray(size);
            } else {
              skipRemaining = size - buffer.length;
              buffer = new Uint8Array(0);
              return;
            }
            continue;
          }

          if (buffer.length < size) return;
          const box = new DataView(buffer.buffer, buffer.byteOffset, size);

          if (type === "moov" && timescale === null) {
            const at = findBox(box, "mdhd");
            if (at >= 0) {
              const version = box.getUint8(at);

              const off = at + 4 + (version === 1 ? 16 : 8);
              if (off + 4 <= size) timescale = box.getUint32(off) || null;
            }
          } else if (type === "moof" && timescale) {
            const at = findBox(box, "tfdt");
            if (at >= 0) {
              const version = box.getUint8(at);
              const base = version === 1 ? Number(box.getBigUint64(at + 4)) : box.getUint32(at + 4);
              mediaSeconds = base / timescale;
            }
          }

          buffer = buffer.subarray(size);
        }
      } catch {
        broken = true;
      }
    },
    get seconds(): number | null {
      return broken || timescale === null ? null : mediaSeconds;
    },
  };
}

const BLOB_FOLD_BYTES = 32 * 1024 * 1024;

function sanitizeFilenamePart(value: string): string {
  return value
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function toFilenameStamp(value: string): string {
  return value.length === 14 ? value.slice(2, 12) : value;
}

function buildDownloadFilename(
  siteName: string | undefined,
  cctvName: string | undefined,
  startDate: string,
  endDate: string
): string {
  const parts = [siteName, cctvName, toFilenameStamp(startDate), toFilenameStamp(endDate)]
    .map((v) => sanitizeFilenamePart(v ?? ""))
    .filter(Boolean);
  return `${parts.join("_")}.mp4`;
}

async function downloadRecording(
  cctv: Pick<Cctv, "id" | "name"> & { site?: { name?: string } | null },
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal; onProgress?: (p: DownloadProgress) => void } = {}
): Promise<void> {
  const { signal, onProgress } = options;
  const res = await cctvService.download(cctv.id, startDate, endDate, { signal });

  if (!res.body) throw new ApiError(500, "응답 본문이 비어 있습니다.");

  const totalSeconds = rangeMinutesOf(startDate, endDate) * 60;
  const clock = createFragmentClock();
  const reader = res.body.getReader();
  const parts: BlobPart[] = [];
  let pending = 0;
  let receivedBytes = 0;

  let projectedBytes: number | null = null;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      clock.push(value);
      parts.push(value);
      pending += value.byteLength;
      receivedBytes += value.byteLength;

      if (pending >= BLOB_FOLD_BYTES) {
        parts.splice(0, parts.length, new Blob(parts));
        pending = 0;
      }
      const seconds = clock.seconds;
      const known = seconds !== null && seconds > 0 && totalSeconds > 0;

      if (projectedBytes === null && known && seconds >= 3) {
        projectedBytes = (receivedBytes / seconds) * totalSeconds;
        if (projectedBytes > MAX_DOWNLOAD_BYTES) {
          reader.cancel().catch(() => {});
          throw new ApiError(
            413,
            `이 카메라는 화질이 높아 선택한 구간이 약 ${Math.round(projectedBytes / 1024 / 1024 / 1024)}GB 입니다. 구간을 줄여 다시 받아주세요`
          );
        }
      }

      onProgress?.({
        receivedBytes,
        ratio: known ? Math.min(1, seconds / totalSeconds) : null,
        projectedBytes,
      });
    }
  } catch (err) {
    reader.cancel().catch(() => {});
    throw err;
  }

  const blob = new Blob(parts, { type: "video/mp4" });
  const name = buildDownloadFilename(cctv.site?.name, cctv.name, startDate, endDate);

  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();

  window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}

function rangeMinutesOf(startDate: string, endDate: string): number {
  const toDate = (v: string) =>
    new Date(
      Number(v.slice(0, 4)),
      Number(v.slice(4, 6)) - 1,
      Number(v.slice(6, 8)),
      Number(v.slice(8, 10)),
      Number(v.slice(10, 12)),
      Number(v.slice(12, 14))
    );
  return (toDate(endDate).getTime() - toDate(startDate).getTime()) / 60000;
}

export const cctvMedia = {
  getWHEPUrl,
  downloadRecording,
  estimateDownload,
  MAX_DOWNLOAD_MINUTES,
};
